import React, { useEffect, useState } from 'react';
import {
    Box, Flex, Heading, Text, Badge, Button, Tabs, TabList, Tab,
    TabPanels, TabPanel, Code, Alert, AlertIcon, AlertDescription,
    useColorModeValue, useToast, IconButton, Divider, Stack,
    HStack, Tooltip, Spinner, useDisclosure, Modal, ModalOverlay,
    ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    Tag, TagLabel, TagLeftIcon, Avatar,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    MdContentCopy, MdCheck, MdRefresh, MdArrowBack, MdOpenInNew,
    MdLock, MdWarning, MdCheckCircle, MdInfo,
} from 'react-icons/md';
import axiosClient from '../../../axios-client';
import { useStateContext } from '../../../context/ContextProvider';

// ─── Per-API documentation content ──────────────────────────────────────────
const getApiDocs = (t) => ({
    woocommerce: {
        title: t("WooCommerce / WordPress"),
        color: 'purple',
        steps: [
            {
                step: 1,
                title: t("Accédez à votre tableau de bord WordPress"),
                content: 'Connectez-vous à votre site WordPress en tant qu\'administrateur.',
            },
            {
                step: 2,
                title: t("Naviguez vers WooCommerce → Paramètres → Avancé → Webhooks"),
                content: 'Allez dans WooCommerce > Paramètres > Avancé > onglet "Webhooks".',
            },
            {
                step: 3,
                title: t("Créez un nouveau webhook"),
                content: 'Cliquez sur "Ajouter un webhook" puis configurez-le:\n• Nom : Ecolinke Orders\n• Statut : Actif\n• Sujet : Order created\n• URL de livraison : [votre URL webhook]\n• Version API : Dernière version',
            },
            {
                step: 4,
                title: t("Collez votre URL et votre Secret"),
                content: '• Collez l\'URL webhook ci-dessus dans "URL de livraison".\n• Collez votre Token Secret ci-dessous dans le champ "Secret".\n• Enregistrez.',
            },
            {
                step: 5,
                title: t("Testez la connexion"),
                content: 'WooCommerce enverra un ping de test. Vérifiez que la connexion est réussie dans votre tableau de bord Ecolinke.',
            },
        ],
        examplePayload: `{
  "id": 1234,
  "status": "processing",
  "billing": {
    "first_name": "Ahmed",
    "last_name": "Benali",
    "email": "ahmed@example.com",
    "phone": "0612345678",
    "address_1": "123 Rue Example",
    "city": "Casablanca",
    "country": "MA"
  },
  "line_items": [
    {
      "name": "Produit Example",
      "sku": "PROD-001",
      "quantity": 2,
      "price": "99.00"
    }
  ]
}`,
        commonErrors: [
            { code: '401 Unauthorized', solution: 'Vérifiez que l\'URL webhook contient le bon token.' },
            { code: '403 Subscription expired', solution: 'Renouvelez votre abonnement Ecolinke.' },
            { code: 'Signature mismatch', solution: 'Assurez-vous que le secret WooCommerce correspond.' },
        ],
    },
    google_sheets: {
        title: t("Google Sheets"),
        color: 'teal',
        steps: [
            {
                step: 1,
                title: t("Ouvrez votre Google Sheets"),
                content: 'Ouvrez le fichier Google Sheets contenant vos commandes.',
            },
            {
                step: 2,
                title: t("Accédez à Apps Script"),
                content: 'Menu: Extensions → Apps Script. Un éditeur de code s\'ouvre.',
            },
            {
                step: 3,
                title: t("Collez le script de webhook"),
                content: 'Collez le script fourni ci-dessous dans l\'éditeur et remplacez WEBHOOK_URL par votre URL.',
            },
            {
                step: 4,
                title: t("Configurez le déclencheur"),
                content: 'Dans Apps Script: Déclencheurs → Ajouter un déclencheur → onEdit → Sur modification.',
            },
            {
                step: 5,
                title: t("Autorisez le script"),
                content: 'Lors du premier lancement, autorisez le script à accéder à votre compte Google.',
            },
        ],
        scriptTemplate: `

var WEBHOOK_URL = "__WEBHOOK_URL__";

function syncNewOrders() {
  var sheet = SpreadsheetApp.getActiveSpreadsheet().getActiveSheet();
  var dataRange = sheet.getDataRange();
  var data = dataRange.getValues();
  
  if (data.length <= 1) return; // Stop if only headers
  
  // Find column indexes based on header names (row 0)
  var headers = data[0].map(function(h) { return h.toString().toLowerCase().trim(); });
  
  // Create a tracking column if it doesn't exist
  var statusColIndex = headers.indexOf("status_sync");
  if (statusColIndex === -1) {
    statusColIndex = headers.length;
    sheet.getRange(1, statusColIndex + 1).setValue("status_sync");
  }
  
  // Column mapping Dictionary
  var colMap = {
    nom_complet: headers.indexOf("nom complet"),
    order_id: headers.indexOf("id commande"),
    email: headers.indexOf("email"),
    pays: headers.indexOf("pays"),
    ville: headers.indexOf("ville"),
    adresse: headers.indexOf("adresse"),
    num_telephone: headers.indexOf("num téléphone"),
    code_produit: headers.indexOf("code produit"),
    couleur: headers.indexOf("couleur"),
    taille: headers.indexOf("taille"),
    quantite: headers.indexOf("quantité"),
    prix_de_vente: headers.indexOf("prix de vente"),
    code_coupon: headers.indexOf("code coupon")
  };

  for (var i = 1; i < data.length; i++) {
    var row = data[i];
    var status = row[statusColIndex];
    
    // Only send rows that haven't been successfully synced yet
    if (status !== "Synced") {
      
      // Basic Validation: Skip rows missing essential phone / product_code
      if (!row[colMap.num_telephone] || !row[colMap.code_produit]) {
        continue;
      }

      // Build JSON payload matching the keys the Django server expects
      var payload = {
        "nom complet": row[colMap.nom_complet] || "",
        "id commande": row[colMap.order_id] || "",
        "email": row[colMap.email] || "",
        "pays": row[colMap.pays] || "",
        "ville": row[colMap.ville] || "",
        "adresse": row[colMap.adresse] || "",
        "num téléphone": row[colMap.num_telephone] || "",
        "code produit": row[colMap.code_produit] || "",
        "couleur": row[colMap.couleur] || "",
        "taille": row[colMap.taille] || "",
        "quantité": row[colMap.quantite] || 1,
        "prix de vente": row[colMap.prix_de_vente] || 0.0,
        "code coupon": row[colMap.code_coupon] || ""
      };
      
      var options = {
        method: "post",
        contentType: "application/json",
        payload: JSON.stringify(payload),
        muteHttpExceptions: true // Capture detailed error responses
      };
      
      try {
        var response = UrlFetchApp.fetch(WEBHOOK_URL, options);
        var responseCode = response.getResponseCode();
        var jsonResponse = JSON.parse(response.getContentText());

        if (responseCode === 200 || jsonResponse.status === "skipped") {
          // Success (200) or already processed via duplicate check
          sheet.getRange(i + 1, statusColIndex + 1).setValue("Synced");
          
        } else {
          // Failure with readable custom python logs
          var errMsg = jsonResponse.error || ("Code: " + responseCode);
          sheet.getRange(i + 1, statusColIndex + 1).setValue("Failed: " + errMsg);
        }
      } catch (e) {
        // Core Exception (e.g., DNS down, invalid URL)
        sheet.getRange(i + 1, statusColIndex + 1).setValue("Error: " + e.message);
      }
    }
  }
}


function onOpen() {
  var ui = SpreadsheetApp.getUi();
  ui.createMenu('🛍️ Integration')
    .addItem('🚀 Sync Orders to Django', 'syncNewOrders')
    .addToUi();
}
  `,
        examplePayload: `{
  "nom complet": "Ahmed Benali",
  "id commande": "ORD-001",
  "email": "ahmed@example.com",
  "num téléphone": "0612345678",
  "pays": "Maroc",
  "ville": "Casablanca",
  "adresse": "123 Rue Example",
  "code produit": "PROD-001",
  "quantité": 2,
  "prix de vente": 99.00,
  "couleur": "Rouge",
  "taille": "M"
}`,
        commonErrors: [
            { code: 'TypeError: Cannot read property', solution: 'Vérifiez que les colonnes correspondent à l\'ordre attendu.' },
            { code: '401 Unauthorized', solution: 'Recopiez l\'URL webhook, le token est peut-être invalide.' },
            { code: '403 Subscription expired', solution: 'Renouvelez votre abonnement Ecolinke.' },
        ],
    },
    shopify: {
        title: t("Shopify"),
        color: 'green',
        steps: [
            { step: 1, title: t("Accédez à votre admin Shopify"), content: 'Connectez-vous à votre boutique Shopify.' },
            { step: 2, title: t("Paramètres → Notifications"), content: 'Allez dans Paramètres → Notifications → Webhooks.' },
            { step: 3, title: t("Créer un webhook"), content: 'Cliquez sur "Créer un webhook":\n• Événement: Création de commande\n• Format: JSON\n• URL: [votre URL webhook]' },
            { step: 4, title: t("Enregistrez"), content: 'Cliquez sur Enregistrer. Shopify enverra automatiquement les nouvelles commandes.' },
            { step: 5, title: t("Testez"), content: 'Passez une commande test dans votre boutique et vérifiez qu\'elle apparaît dans Ecolinke.' },
        ],
        examplePayload: `{
  "id": 9876543210,
  "order_number": 1001,
  "financial_status": "paid",
  "customer": {
    "first_name": "Fatima",
    "last_name": "Alaoui",
    "email": "fatima@example.com",
    "phone": "+212612345678"
  },
  "shipping_address": {
    "address1": "456 Boulevard Hassan",
    "city": "Rabat",
    "country_code": "MA"
  },
  "line_items": [...]
}`,
        commonErrors: [
            { code: 'HMAC validation failed', solution: 'Vérifiez que le token dans l\'URL est correct.' },
            { code: '403 Subscription', solution: 'Renouvelez votre abonnement.' },
        ],
    },
    lightfunnels: {
        title: t("Lightfunnels"),
        color: 'orange',
        steps: [
            { step: 1, title: t("Accédez à votre compte Lightfunnels"), content: 'Connectez-vous à app.lightfunnels.com.' },
            { step: 2, title: t("Paramètres → Intégrations → Webhooks"), content: 'Naviguez vers les paramètres de votre entonnoir.' },
            { step: 3, title: t("Ajoutez le webhook"), content: 'Collez l\'URL webhook dans le champ "Order Created Webhook".' },
            { step: 4, title: t("Activez et testez"), content: 'Activez le webhook et effectuez une commande test.' },
        ],
        examplePayload: `{
  "id": "lf_order_123",
  "status": "completed",
  "customer": { "name": "...", "email": "...", "phone": "..." },
  "products": [...]
}`,
        commonErrors: [
            { code: '401', solution: 'Vérifiez le token dans l\'URL.' },
        ],
    },
    storeep: {
        title: t("Storeep"),
        color: 'blue',
        steps: [
            { step: 1, title: t("Connectez-vous à Storeep"), content: 'Accédez au tableau de bord de votre boutique Storeep.' },
            { step: 2, title: t("Paramètres → Webhooks"), content: 'Trouvez la section Webhooks dans les paramètres.' },
            { step: 3, title: t("Ajoutez l'URL"), content: 'Collez votre URL webhook dans le champ prévu.' },
            { step: 4, title: t("Activez"), content: 'Activez le webhook et testez avec une commande.' },
        ],
        examplePayload: `{
  "order_id": "ST-1234",
  "customer_name": "...",
  "phone": "...",
  "products": [...]
}`,
        commonErrors: [
            { code: '401', solution: 'Vérifiez le token.' },
        ],
    },
});

// ─── CopyBlock ─────────────────────────────────────────────────────────────
function CopyBlock({ value, label, mono = true }) {
    const [copied, setCopied] = useState(false);
    const toast = useToast();
    const { t } = useTranslation();
    const bg = useColorModeValue('gray.100', 'gray.600');

    const handleCopy = () => {
        navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
        toast({ description: `${label} copié !`, status: 'success', duration: 1500, position: 'bottom-right' });
    };

    return (
        <Flex
            bg={bg}
            rounded="lg"
            p={3}
            align="center"
            gap={3}
            border="1px solid"
            borderColor={useColorModeValue('gray.200', 'gray.500')}
        >
            <Box flex={1} overflow="hidden">
                {mono
                    ? <Code fontSize="sm" wordBreak="break-all" bg="transparent">{value}</Code>
                    : <Text fontSize="sm" wordBreak="break-all">{value}</Text>
                }
            </Box>
            <Tooltip label={copied ? 'Copié !' : `Copier ${label}`}>
                <IconButton
                    icon={copied ? <MdCheck /> : <MdContentCopy />}
                    size="sm"
                    colorScheme={copied ? 'green' : 'gray'}
                    variant="ghost"
                    onClick={handleCopy}
                    aria-label={t("copier")}
                    flexShrink={0}
                />
            </Tooltip>
        </Flex>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function APIDetail() {
    const { t } = useTranslation();
    const { user } = useStateContext();
    const { state } = useLocation();
    const navigate = useNavigate();
    const toast = useToast();

    const [connection, setConnection] = useState(state?.connection || null);
    const [apiType] = useState(state?.apiType || null);
    const [regenerating, setRegenerating] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();

    const docs = getApiDocs(t)[apiType?.code] || {};
    const colorScheme = docs.color || 'teal';
    const subText = useColorModeValue('gray.500', 'gray.400');
    const cardBg = useColorModeValue('white', 'gray.700');
    const borderC = useColorModeValue('gray.200', 'gray.600');
    const bgStatistics = useColorModeValue('gray.50', 'gray.600');
    const bgBoxGoogleSheet = useColorModeValue('gray.900', 'gray.800');


    if (!connection || !apiType) {
        return (
            <Flex justify="center" align="center" minH="300px" direction="column" gap={4}>
                <Text>{t('Aucune connexion sélectionnée.')}</Text>
                <Button onClick={() => navigate('/mes-apis')}>{t('Retour aux APIs')}</Button>
            </Flex>
        );
    }

    const webhookUrl = connection.webhook_url || `[Recalcul en cours...]`;

    // ── Regenerate Token ─────────────────────────────────────────────────
    const handleRegenerateToken = async () => {
        setRegenerating(true);
        try {
            const res = await axiosClient.post(
                `/api-connections/${connection.id}/regenerate_token/?user_id=${user.id}`
            );
            setConnection((prev) => ({ ...prev, ...res.data }));
            toast({
                description: t('Token régénéré avec succès. Mettez à jour votre webhook !'),
                status: 'success',
                duration: 4000,
                isClosable: true,
                position: 'bottom-right',
            });
        } catch (err) {
            toast({ description: t('Erreur lors de la régénération.'), status: 'error', duration: 3000, position: 'bottom-right' });
        }
        setRegenerating(false);
        onClose();
    };

    return (
        <Box p={{ base: 4, md: 8 }} maxW="900px" mx="auto">
            {/* ── Back button ────────────────────────────────────────────── */}
            <Button
                leftIcon={<MdArrowBack />}
                variant="ghost"
                mb={6}
                onClick={() => navigate('/mes-apis')}
                size="sm"
            >
                {t('Retour aux Sources de Commandes')}
            </Button>

            {/* ── Header card ───────────────────────────────────────────── */}
            <Box bg={cardBg} border="1px solid" borderColor={borderC} rounded="2xl" p={6} mb={6} shadow="sm">
                <Flex justify="space-between" align="flex-start" direction={{ base: 'column', md: 'row' }} gap={4}>
                    <Box>
                        <Flex align="center" gap={3} mb={2}>
                            {apiType.logo && (
                                <Avatar src={apiType.logo} name={apiType.name} size="md" rounded="xl" bg={`${colorScheme}.500`} color="white" shadow="sm" />
                            )}
                            <Heading size="lg">{apiType.name}</Heading>
                            <Badge colorScheme={connection.is_active ? 'green' : 'red'} rounded="full" px={3}>
                                {connection.is_active ? t('Actif') : t('Inactif')}
                            </Badge>
                        </Flex>
                        <Text color={subText} fontSize="sm">{apiType.description}</Text>
                    </Box>
                    <HStack>
                        <Tag colorScheme={colorScheme} size="md" rounded="full">
                            <TagLeftIcon as={MdCheckCircle} />
                            <TagLabel>{connection.orders_imported} {t('commandes')}</TagLabel>
                        </Tag>
                    </HStack>
                </Flex>
            </Box>

            {/* ── Main Tabs ──────────────────────────────────────────────── */}
            <Tabs colorScheme={colorScheme} variant="enclosed-colored">
                <TabList flexWrap="wrap">
                    <Tab>{t('🔗 Connexion')}</Tab>
                    <Tab>{t('📖 Documentation')}</Tab>
                    <Tab>{t('📦 Payload exemple')}</Tab>
                    <Tab>{t('❗ Erreurs courantes')}</Tab>
                </TabList>

                <TabPanels>

                    {/* ── Tab 1: Connection Info ──────────────────────────── */}
                    <TabPanel px={0} pt={6}>
                        <Stack spacing={6}>
                            {/* Webhook URL */}
                            <Box>
                                <Text fontWeight="bold" mb={2} fontSize="sm" textTransform="uppercase" color={subText}>
                                    {t('🌐 URL Webhook')}
                                </Text>
                                <CopyBlock value={webhookUrl} label="URL Webhook" />
                                <Text fontSize="xs" color={subText} mt={1}>
                                    {t('Collez cette URL dans les paramètres webhook de votre plateforme.')}
                                </Text>
                            </Box>

                            <Divider />

                            {/* Token */}
                            <Box>
                                <Flex justify="space-between" align="center" mb={2}>
                                    <Text fontWeight="bold" fontSize="sm" textTransform="uppercase" color={subText}>
                                        🔑 {t('Token secret')}
                                    </Text>
                                    <Button
                                        size="xs"
                                        leftIcon={<MdRefresh />}
                                        colorScheme="orange"
                                        variant="outline"
                                        onClick={onOpen}
                                    >
                                        {t('Régénérer')}
                                    </Button>
                                </Flex>
                                <CopyBlock value={connection.token} label="Token" />
                                <Alert status="warning" mt={2} rounded="md" py={2}>
                                    <AlertIcon as={MdWarning} />
                                    <AlertDescription fontSize="xs">
                                        {t('Ne partagez jamais ce token. Si compromis, régénérez-le immédiatement et mettez à jour votre webhook.')}
                                    </AlertDescription>
                                </Alert>
                            </Box>

                            <Divider />

                            {/* Stats */}
                            <Box>
                                <Text fontWeight="bold" mb={3} fontSize="sm" textTransform="uppercase" color={subText}>
                                    📊 {t('Statistiques')}
                                </Text>
                                <Flex gap={6} flexWrap="wrap">
                                    {[
                                        { label: t('Commandes importées'), value: connection.orders_imported },
                                        {
                                            label: t('Créé le'),
                                            value: new Date(connection.created_at).toLocaleDateString(),
                                        },
                                        {
                                            label: t('Dernière utilisation'),
                                            value: connection.last_used
                                                ? new Date(connection.last_used).toLocaleString()
                                                : t('Jamais'),
                                        },
                                    ].map((item) => (
                                        <Box key={item.label} bg={bgStatistics} p={4} rounded="xl" minW="140px">
                                            <Text fontWeight="bold" fontSize="lg">{item.value}</Text>
                                            <Text fontSize="xs" color={subText}>{item.label}</Text>
                                        </Box>
                                    ))}
                                </Flex>
                            </Box>
                        </Stack>
                    </TabPanel>

                    {/* ── Tab 2: Documentation ───────────────────────────── */}
                    <TabPanel px={0} pt={6}>
                        <Stack spacing={5}>
                            <Heading size="md">{t('Guide d\'intégration')} — {t(docs.title || '')}</Heading>
                            {(docs.steps || []).map((s) => (
                                <Box
                                    key={s.step}
                                    bg={cardBg}
                                    border="1px solid"
                                    borderColor={borderC}
                                    rounded="xl"
                                    p={5}
                                >
                                    <Flex gap={4} align="flex-start">
                                        <Box
                                            bg={`${colorScheme}.500`}
                                            color="white"
                                            rounded="full"
                                            w={8}
                                            h={8}
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            fontWeight="bold"
                                            fontSize="sm"
                                            flexShrink={0}
                                        >
                                            {s.step}
                                        </Box>
                                        <Box>
                                            <Text fontWeight="semibold" mb={1}>{t(s.title)}</Text>
                                            <Text fontSize="sm" color={subText} whiteSpace="pre-line">
                                                {t(s.content)}
                                            </Text>
                                        </Box>
                                    </Flex>
                                </Box>
                            ))}

                            {/* Google Sheets Script Template */}
                            {apiType.code === 'google_sheets' && docs.scriptTemplate && (
                                <Box>
                                    <Text fontWeight="bold" mb={2}>{t('Script Google Apps Script')} :</Text>
                                    <Box bg={bgBoxGoogleSheet} p={4} rounded="xl" position="relative">
                                        <Code
                                            display="block"
                                            whiteSpace="pre"
                                            fontSize="xs"
                                            color="green.300"
                                            bg="transparent"
                                            overflowX="auto"
                                        >
                                            {docs.scriptTemplate.replace('__WEBHOOK_URL__', webhookUrl)}
                                        </Code>
                                        <Button
                                            position="absolute"
                                            top={2}
                                            right={2}
                                            size="xs"
                                            onClick={() =>
                                                navigator.clipboard.writeText(
                                                    docs.scriptTemplate.replace('__WEBHOOK_URL__', webhookUrl)
                                                )
                                            }
                                        >
                                            <MdContentCopy />
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Stack>
                    </TabPanel>

                    {/* ── Tab 3: Example Payload ─────────────────────────── */}
                    <TabPanel px={0} pt={6}>
                        <Heading size="md" mb={4}>{t('Exemple de payload reçu')}</Heading>
                        <Alert status="info" rounded="lg" mb={4} py={2}>
                            <AlertIcon as={MdInfo} />
                            <AlertDescription fontSize="sm">
                                {t('C\'est la structure JSON qu\'Ecolinke s\'attend à recevoir de votre plateforme.')}
                            </AlertDescription>
                        </Alert>
                        <Box
                            bg={bgBoxGoogleSheet}
                            p={4}
                            rounded="xl"
                            position="relative"
                        >
                            <Code
                                display="block"
                                whiteSpace="pre"
                                fontSize="xs"
                                color="cyan.300"
                                bg="transparent"
                                overflowX="auto"
                            >
                                {docs.examplePayload || '{}'}
                            </Code>
                            <Button
                                position="absolute"
                                top={2}
                                right={2}
                                size="xs"
                                onClick={() => navigator.clipboard.writeText(docs.examplePayload || '{}')}
                            >
                                <MdContentCopy />
                            </Button>
                        </Box>
                    </TabPanel>

                    {/* ── Tab 4: Common Errors ──────────────────────────── */}
                    <TabPanel px={0} pt={6}>
                        <Heading size="md" mb={4}>{t('Erreurs courantes et solutions')}</Heading>
                        <Stack spacing={3}>
                            {(docs.commonErrors || []).map((err, i) => (
                                <Box
                                    key={i}
                                    bg={cardBg}
                                    border="1px solid"
                                    borderColor="red.200"
                                    rounded="xl"
                                    p={4}
                                >
                                    <Flex gap={3} align="flex-start">
                                        <Box color="red.400" mt={0.5} flexShrink={0}><MdWarning size={20} /></Box>
                                        <Box>
                                            <Code colorScheme="red" mb={1} fontSize="sm">{err.code}</Code>
                                            <Text fontSize="sm" color={subText}>{t(err.solution)}</Text>
                                        </Box>
                                    </Flex>
                                </Box>
                            ))}
                        </Stack>
                    </TabPanel>
                </TabPanels>
            </Tabs>

            {/* ── Regenerate Token Confirmation ─────────────────────────── */}
            <Modal isOpen={isOpen} onClose={onClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <Flex align="center" gap={2}><MdLock /> {t('Régénérer le token')}</Flex>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Alert status="error" rounded="md" mb={4}>
                            <AlertIcon />
                            <AlertDescription fontSize="sm">
                                {t('Attention ! Après régénération, l\'ancien token sera invalide. Vous devrez mettre à jour l\'URL webhook sur votre plateforme !')}
                            </AlertDescription>
                        </Alert>
                        <Text fontSize="sm">{t('Êtes-vous sûr de vouloir générer un nouveau token secret ?')}</Text>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <Button
                            colorScheme="orange"
                            isLoading={regenerating}
                            leftIcon={<MdRefresh />}
                            onClick={handleRegenerateToken}
                        >
                            {t('Régénérer')}
                        </Button>
                        <Button variant="ghost" onClick={onClose}>{t('Annuler')}</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}
